import React from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon, CsvIcon } from '~/assets';
import { FileCheckIcon, FileTypeFailedIcon } from '~/assets/FileChooser';
import { CircularProgress } from '~/components';

import { AttachmentError, ChipButton, FileCard, FileName, FileStatus, FileText } from './ComposerAttachment.styles';
import { ComposerAttachment } from './useComposerAttachment';

interface AttachmentChipProps {
  attachment: ComposerAttachment | null;
  isNotCsv: boolean;
  onRetry: () => void;
  onRemove: () => void;
}

/** The file waiting in the composer, with its upload state. */
const AttachmentChip = ({ attachment, isNotCsv, onRetry, onRemove }: AttachmentChipProps): JSX.Element | null => {
  const { t } = useTranslation();
  if (!attachment) {
    return isNotCsv ? <AttachmentError>{t('aiInsights.attachments.onlyCsv')}</AttachmentError> : null;
  }

  const { file, status, progress } = attachment;
  return (
    <>
      <FileCard $failed={status === 'failed'}>
        {status === 'failed' ? <FileTypeFailedIcon /> : <CsvIcon />}
        <FileText>
          <FileName title={file.name}>{file.name}</FileName>
          <FileStatus>
            {status === 'uploading' && t('aiInsights.attachments.uploading', { progress })}
            {status === 'failed' && t('aiInsights.attachments.uploadFailed')}
            {status === 'ready' && t('aiInsights.attachments.ready')}
          </FileStatus>
        </FileText>
        {status === 'uploading' && <CircularProgress size={24} value={progress} />}
        {status === 'ready' && <FileCheckIcon />}
        {status === 'failed' && (
          <ChipButton type="button" onClick={onRetry}>
            {t('aiInsights.attachments.retry')}
          </ChipButton>
        )}
        <ChipButton
          type="button"
          aria-label={t('aiInsights.attachments.remove', { fileName: file.name })}
          onClick={onRemove}
        >
          <CloseIcon />
        </ChipButton>
      </FileCard>
      {isNotCsv && <AttachmentError>{t('aiInsights.attachments.onlyCsv')}</AttachmentError>}
    </>
  );
};

export default AttachmentChip;
